<template>
  <Layout>
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="page-title-box d-flex align-items-center justify-content-between">
            <h4 class="mb-0">Dashboard de Bilheteria</h4>
            <div class="d-flex align-items-center">
              <select v-model="periodFilter" class="form-control form-control-sm mr-2" style="width: 180px;" @change="loadDashboard">
                <option value="7">Últimos 7 dias</option>
                <option value="15">Últimos 15 dias</option>
                <option value="30">Últimos 30 dias</option>
                <option value="60">Últimos 60 dias</option>
                <option value="90">Últimos 90 dias</option>
              </select>
              <button class="btn btn-sm btn-primary" @click="loadDashboard">
                <i class="mdi mdi-refresh"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <loading
        :active.sync="loading"
        color="#556ee6"
        loader="dots"
        :can-cancel="false"
        :is-full-page="false">
      </loading>

      <!-- KPIs principais -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Receita Total</p>
                  <h4 class="mb-0">{{ formatMoney(kpis.totalRevenue) }}</h4>
                  <small class="text-success">
                    <i class="mdi mdi-arrow-up"></i> {{ kpis.revenueGrowth }}%
                  </small>
                </div>
                <div class="avatar-sm bg-primary-soft align-self-center">
                  <i class="mdi mdi-cash-multiple text-primary font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Ingressos Vendidos</p>
                  <h4 class="mb-0">{{ kpis.totalTicketsSold }}</h4>
                  <small class="text-muted">
                    de {{ kpis.totalTickets }} disponíveis
                  </small>
                </div>
                <div class="avatar-sm bg-success-soft align-self-center">
                  <i class="mdi mdi-ticket text-success font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Ticket Médio</p>
                  <h4 class="mb-0">{{ formatMoney(kpis.averageTicket) }}</h4>
                  <small class="text-muted">
                    por transação
                  </small>
                </div>
                <div class="avatar-sm bg-info-soft align-self-center">
                  <i class="mdi mdi-chart-line text-info font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-3 col-md-6">
          <div class="card border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="flex-grow-1">
                  <p class="text-muted mb-2">Taxa de Conversão</p>
                  <h4 class="mb-0">{{ kpis.conversionRate }}%</h4>
                  <small class="text-muted">
                    {{ kpis.totalOrders }} pedidos
                  </small>
                </div>
                <div class="avatar-sm bg-warning-soft align-self-center">
                  <i class="mdi mdi-chart-donut text-warning font-size-24"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Gráficos -->
      <div class="row">
        <div class="col-xl-8">
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-4">
                <h4 class="card-title mb-0">Evolução de Vendas</h4>
                <div>
                  <button
                    class="btn btn-sm"
                    :class="revenueChartType === 'revenue' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="revenueChartType = 'revenue'"
                  >
                    Receita
                  </button>
                  <button
                    class="btn btn-sm ml-2"
                    :class="revenueChartType === 'tickets' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="revenueChartType = 'tickets'"
                  >
                    Ingressos
                  </button>
                </div>
              </div>
              <apexchart
                type="area"
                height="350"
                :options="revenueChartOptions"
                :series="revenueChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>

        <div class="col-xl-4">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title mb-4">Métodos de Pagamento</h4>
              <apexchart
                type="bar"
                height="350"
                :options="paymentMethodChartOptions"
                :series="paymentMethodChartSeries"
              ></apexchart>
            </div>
          </div>
        </div>
      </div>

      <!-- Ingressos Mais Vendidos -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title mb-4">Ingressos Mais Vendidos</h4>
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>Ingresso</th>
                      <th>Vendidos</th>
                      <th>Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ticket in topTickets" :key="ticket.id">
                      <td>
                        <div class="d-flex align-items-center">
                          <div class="avatar-xs mr-3 bg-primary-soft d-flex align-items-center justify-content-center">
                            <i class="mdi mdi-ticket text-primary font-size-16"></i>
                          </div>
                          <div>
                            <div>{{ ticket.name }}</div>
                            <small class="text-muted">{{ formatMoney(ticket.price) }}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <strong>{{ ticket.quantity }}</strong>
                        <div class="progress" style="height: 4px;">
                          <div
                            class="progress-bar bg-primary"
                            :style="{ width: ticket.percentage + '%' }"
                          ></div>
                        </div>
                      </td>
                      <td><strong>{{ formatMoney(ticket.revenue) }}</strong></td>
                    </tr>
                    <tr v-if="topTickets.length === 0 && !loading">
                      <td colspan="3" class="text-center text-muted py-3">
                        Nenhum dado disponível
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grid de Transações -->
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between mb-4">
                <h4 class="card-title mb-0">Últimas Transações</h4>
                <div class="d-flex align-items-center">
                  <input
                    v-model="transactionSearch"
                    type="text"
                    class="form-control form-control-sm mr-2"
                    placeholder="Buscar por nome, email..."
                    style="width: 250px;"
                  >
                  <button class="btn btn-sm btn-primary" @click="loadRecentTransactions">
                    <i class="mdi mdi-refresh"></i>
                  </button>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="thead-light">
                    <tr>
                      <th>ID Transação</th>
                      <th>Data</th>
                      <th>Comprador</th>
                      <th>Valor</th>
                      <th>Método</th>
                      <th>Status</th>
                      <th class="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="transaction in filteredTransactions" :key="transaction.id">
                      <td>
                        <span class="font-weight-medium">#{{ transaction.id }}</span>
                      </td>
                      <td>
                        <div>{{ formatDate(transaction.created_at) }}</div>
                        <small class="text-muted">{{ formatTime(transaction.created_at) }}</small>
                      </td>
                      <td>
                        <div>{{ transaction.customer_name }}</div>
                        <small class="text-muted">{{ transaction.customer_email }}</small>
                      </td>
                      <td>
                        <strong class="text-primary">{{ formatMoney(transaction.amount) }}</strong>
                      </td>
                      <td>
                        <span class="badge" :class="'badge-' + getPaymentMethodColor(transaction.payment_method)">
                          {{ getPaymentMethodLabel(transaction.payment_method) }}
                        </span>
                      </td>
                      <td>
                        <span class="badge badge-pill" :class="'badge-' + getStatusColor(transaction.status)">
                          {{ getStatusLabel(transaction.status) }}
                        </span>
                      </td>
                      <td class="text-center">
                        <button
                          class="btn btn-sm btn-outline-primary"
                          @click="openTransactionDetails(transaction)"
                          title="Ver detalhes"
                        >
                          <i class="mdi mdi-eye"></i>
                        </button>
                      </td>
                    </tr>
                    <tr v-if="filteredTransactions.length === 0 && !loading">
                      <td colspan="7" class="text-center text-muted py-4">
                        Nenhuma transação encontrada
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Paginação -->
              <div v-if="totalTransactions > transactionPerPage" class="row mt-4">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center">
                    <p class="mb-0 text-muted">
                      Mostrando {{ (transactionPage - 1) * transactionPerPage + 1 }}-{{ Math.min(transactionPage * transactionPerPage, totalTransactions) }} de {{ totalTransactions }} transações
                    </p>
                    <ul class="pagination mb-0">
                      <li class="page-item" :class="{ disabled: transactionPage === 1 }">
                        <a class="page-link" href="#" @click.prevent="changeTransactionPage(transactionPage - 1)">Anterior</a>
                      </li>
                      <li
                        v-for="page in displayTransactionPages"
                        :key="page"
                        class="page-item"
                        :class="{ active: page === transactionPage }"
                      >
                        <a class="page-link" href="#" @click.prevent="changeTransactionPage(page)">{{ page }}</a>
                      </li>
                      <li class="page-item" :class="{ disabled: transactionPage === totalTransactionPages }">
                        <a class="page-link" href="#" @click.prevent="changeTransactionPage(transactionPage + 1)">Próximo</a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes da Transação -->
    <b-modal
      v-model="transactionModal"
      title="Detalhes da Transação"
      size="lg"
      ok-only
      ok-title="Fechar"
    >
      <div v-if="selectedTransaction">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">ID da Transação</label>
            <div class="font-weight-medium">#{{ selectedTransaction.id }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Status</label>
            <div>
              <span class="badge badge-pill badge-lg" :class="'badge-' + getStatusColor(selectedTransaction.status)">
                {{ getStatusLabel(selectedTransaction.status) }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Data da Transação</label>
            <div class="font-weight-medium">{{ formatDate(selectedTransaction.created_at) }} às {{ formatTime(selectedTransaction.created_at) }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Valor</label>
            <div class="font-weight-medium text-primary h5 mb-0">{{ formatMoney(selectedTransaction.amount) }}</div>
          </div>
        </div>

        <hr>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Nome do Comprador</label>
            <div class="font-weight-medium">{{ selectedTransaction.customer_name }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">E-mail</label>
            <div>{{ selectedTransaction.customer_email }}</div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Método de Pagamento</label>
            <div>
              <span class="badge" :class="'badge-' + getPaymentMethodColor(selectedTransaction.payment_method)">
                {{ getPaymentMethodLabel(selectedTransaction.payment_method) }}
              </span>
            </div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Quantidade de Ingressos</label>
            <div class="font-weight-medium">{{ selectedTransaction.qty_quota || 1 }}</div>
          </div>
        </div>

        <hr>

        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Ingresso</label>
            <div class="font-weight-medium">{{ selectedTransaction.ticket_name || 'N/A' }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Valor do Ingresso</label>
            <div>{{ formatMoney(selectedTransaction.ticket_amount || 0) }}</div>
          </div>
        </div>

        <div class="row" v-if="selectedTransaction.discount > 0">
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Desconto Aplicado</label>
            <div class="text-success font-weight-medium">- {{ formatMoney(selectedTransaction.discount) }}</div>
          </div>
          <div class="col-md-6 mb-3">
            <label class="text-muted mb-1">Cupom Utilizado</label>
            <div>{{ selectedTransaction.cupom_code || 'N/A' }}</div>
          </div>
        </div>

        <div v-if="selectedTransaction.payment_details" class="mt-4">
          <h5>Informações Adicionais do Pagar.me</h5>
          <hr>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Order ID</label>
              <div class="font-weight-medium small">{{ selectedTransaction.payment_details.order_id || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Charge ID</label>
              <div class="font-weight-medium small">{{ selectedTransaction.payment_details.charge_id || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Gateway ID</label>
              <div class="font-weight-medium small">{{ selectedTransaction.payment_details.gateway_id || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Gateway Status</label>
              <div>{{ selectedTransaction.payment_details.gateway_status || 'N/A' }}</div>
            </div>
          </div>

          <div v-if="selectedTransaction.payment_method === 2" class="row">
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Bandeira</label>
              <div class="text-uppercase">{{ selectedTransaction.payment_details.brand || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Últimos 4 Dígitos</label>
              <div>**** **** **** {{ selectedTransaction.payment_details.last_four_digits || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Nome no Cartão</label>
              <div>{{ selectedTransaction.payment_details.holder_name || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Parcelas</label>
              <div>{{ selectedTransaction.payment_details.installments || 1 }}x</div>
            </div>
            <div class="col-md-6 mb-3" v-if="selectedTransaction.payment_details.acquirer_name">
              <label class="text-muted mb-1">Adquirente</label>
              <div>{{ selectedTransaction.payment_details.acquirer_name }}</div>
            </div>
            <div class="col-md-6 mb-3" v-if="selectedTransaction.payment_details.acquirer_tid">
              <label class="text-muted mb-1">TID</label>
              <div class="small">{{ selectedTransaction.payment_details.acquirer_tid }}</div>
            </div>
          </div>

          <div v-if="selectedTransaction.payment_method === 3" class="row">
            <div class="col-md-12 mb-3">
              <label class="text-muted mb-1">End to End ID</label>
              <div class="font-weight-medium small">{{ selectedTransaction.payment_details.end_to_end_id || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3" v-if="selectedTransaction.payment_details.payer_name">
              <label class="text-muted mb-1">Nome do Pagador</label>
              <div>{{ selectedTransaction.payment_details.payer_name }}</div>
            </div>
            <div class="col-md-6 mb-3" v-if="selectedTransaction.payment_details.payer_document">
              <label class="text-muted mb-1">Documento do Pagador</label>
              <div>{{ selectedTransaction.payment_details.payer_document }}</div>
            </div>
          </div>

          <div v-if="selectedTransaction.payment_method === 1" class="row">
            <div class="col-md-12 mb-3">
              <label class="text-muted mb-1">Código de Barras</label>
              <div class="small font-weight-medium">{{ selectedTransaction.payment_details.barcode || 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="text-muted mb-1">Data de Vencimento</label>
              <div>{{ selectedTransaction.payment_details.due_date ? formatDate(selectedTransaction.payment_details.due_date) : 'N/A' }}</div>
            </div>
            <div class="col-md-6 mb-3" v-if="selectedTransaction.payment_details.nosso_numero">
              <label class="text-muted mb-1">Nosso Número</label>
              <div>{{ selectedTransaction.payment_details.nosso_numero }}</div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import {
  getDashboardKpis,
  getDashboardRevenueChart,
  getDashboardTopTickets,
  getDashboardRecentTransactions,
  getDashboardPaymentMethodChart,
  getDashboardBalance
} from '../recipient/services'

export default {
  components: {
    Layout,
    Loading
  },
  data () {
    return {
      loading: false,
      eventId: 1,
      periodFilter: '30',
      revenueChartType: 'revenue',
      transactionSearch: '',
      transactionPage: 1,
      transactionPerPage: 20,
      totalTransactions: 0,
      transactionModal: false,
      selectedTransaction: null,
      kpis: {
        totalRevenue: 0,
        revenueGrowth: 0,
        totalTicketsSold: 0,
        totalTickets: 0,
        averageTicket: 0,
        conversionRate: 0,
        totalOrders: 0
      },
      balance: {
        available_amount: 0,
        waiting_funds_amount: 0,
        transferred_amount: 0
      },
      topTickets: [],
      recentTransactions: [],
      revenueChartSeries: [{
        name: 'Receita',
        data: []
      }],
      revenueChartOptions: {
        chart: {
          height: 350,
          type: 'area',
          toolbar: {
            show: false
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        xaxis: {
          categories: []
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return 'R$ ' + value.toFixed(2)
            }
          }
        },
        colors: ['#556ee6'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.2
          }
        },
        tooltip: {
          y: {
            formatter: (value) => {
              return 'R$ ' + value.toFixed(2)
            }
          }
        }
      },
      paymentMethodChartSeries: [{
        name: 'Transações',
        data: []
      }],
      paymentMethodChartOptions: {
        chart: {
          type: 'bar',
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 4,
            columnWidth: '55%'
          }
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: []
        },
        colors: ['#556ee6']
      }
    }
  },
  computed: {
    filteredTransactions () {
      if (!this.transactionSearch) {
        return this.recentTransactions
      }
      const search = this.transactionSearch.toLowerCase()
      return this.recentTransactions.filter(t =>
        t.customer_name.toLowerCase().includes(search) ||
        t.customer_email.toLowerCase().includes(search) ||
        t.id.toString().includes(search)
      )
    },
    totalTransactionPages () {
      return Math.ceil(this.totalTransactions / this.transactionPerPage)
    },
    displayTransactionPages () {
      const pages = []
      const maxPages = 5
      let start = Math.max(1, this.transactionPage - Math.floor(maxPages / 2))
      let end = Math.min(this.totalTransactionPages, start + maxPages - 1)

      if (end - start < maxPages - 1) {
        start = Math.max(1, end - maxPages + 1)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      return pages
    }
  },
  watch: {
    revenueChartType () {
      this.loadRevenueChart()
    }
  },
  mounted () {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard () {
      this.loading = true
      try {
        await Promise.all([
          this.loadKPIs(),
          this.loadBalance(),
          this.loadTopTickets(),
          this.loadRecentTransactions(),
          this.loadRevenueChart(),
          this.loadPaymentMethodChart()
        ])
      } catch (error) {
        console.error('Erro ao carregar dashboard:', error)
        this.$toast.error('Erro ao carregar dados do dashboard')
      } finally {
        this.loading = false
      }
    },
    async loadKPIs () {
      try {
        const response = await getDashboardKpis({ days: this.periodFilter })
        this.kpis = response.data
        console.log('KPIs carregados:', this.kpis)
      } catch (error) {
        console.error('Erro ao carregar KPIs:', error)
        // Fallback para valores zerados em caso de erro
        this.kpis = {
          totalRevenue: 0,
          revenueGrowth: 0,
          totalTicketsSold: 0,
          totalTickets: 0,
          averageTicket: 0,
          conversionRate: 0,
          totalOrders: 0
        }
      }
    },
    async loadBalance () {
      try {
        const response = await getDashboardBalance()
        this.balance = response.data
        console.log('Saldo carregado:', this.balance)
      } catch (error) {
        console.error('Erro ao carregar saldo:', error)
        this.balance = {
          available_amount: 0,
          waiting_funds_amount: 0,
          transferred_amount: 0
        }
      }
    },
    async loadTopTickets () {
      try {
        const response = await getDashboardTopTickets({ days: this.periodFilter })
        this.topTickets = response.data
        console.log('Top tickets carregados:', this.topTickets)
      } catch (error) {
        console.error('Erro ao carregar top tickets:', error)
        this.topTickets = []
      }
    },
    async loadRecentTransactions () {
      try {
        const response = await getDashboardRecentTransactions({
          page: this.transactionPage,
          per_page: this.transactionPerPage
        })

        if (response.data.data) {
          this.recentTransactions = response.data.data
          this.totalTransactions = response.data.total || response.data.data.length
        } else {
          this.recentTransactions = response.data
          this.totalTransactions = response.data.length
        }

        console.log('Transações recentes carregadas:', this.recentTransactions)
      } catch (error) {
        console.error('Erro ao carregar transações recentes:', error)
        this.recentTransactions = []
        this.totalTransactions = 0
      }
    },
    async loadRevenueChart () {
      try {
        const response = await getDashboardRevenueChart({
          days: this.periodFilter,
          type: this.revenueChartType
        })

        this.revenueChartOptions = {
          ...this.revenueChartOptions,
          xaxis: {
            categories: response.data.categories
          }
        }

        this.revenueChartSeries = [{
          name: this.revenueChartType === 'revenue' ? 'Receita' : 'Ingressos',
          data: response.data.data
        }]

        console.log('Gráfico de receita carregado:', response.data)
      } catch (error) {
        console.error('Erro ao carregar gráfico de receita:', error)
        this.revenueChartSeries = [{
          name: this.revenueChartType === 'revenue' ? 'Receita' : 'Ingressos',
          data: []
        }]
      }
    },
    async loadPaymentMethodChart () {
      try {
        const response = await getDashboardPaymentMethodChart({ days: this.periodFilter })

        this.paymentMethodChartSeries = [{
          name: 'Transações',
          data: response.data.data
        }]
        this.paymentMethodChartOptions = {
          ...this.paymentMethodChartOptions,
          xaxis: {
            categories: response.data.categories
          }
        }

        console.log('Gráfico de métodos de pagamento carregado:', response.data)
      } catch (error) {
        console.error('Erro ao carregar gráfico de métodos de pagamento:', error)
        this.paymentMethodChartSeries = [{
          name: 'Transações',
          data: []
        }]
      }
    },
    formatMoney (value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0)
    },
    formatDate (date) {
      return this.$moment(date).format('DD/MM/YYYY')
    },
    formatTime (date) {
      return this.$moment(date).format('HH:mm')
    },
    changeTransactionPage (page) {
      if (page >= 1 && page <= this.totalTransactionPages) {
        this.transactionPage = page
        this.loadRecentTransactions()
      }
    },
    getStatusLabel (status) {
      const labels = {
        0: 'Aberto',
        1: 'Pago',
        2: 'Cancelado',
        3: 'Reembolsado',
        4: 'Erro',
        paid: 'Pago',
        pending: 'Pendente',
        failed: 'Falhou',
        refunded: 'Reembolsado',
        canceled: 'Cancelado'
      }
      return labels[status] || status
    },
    getStatusColor (status) {
      const colors = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        3: 'info',
        4: 'danger',
        paid: 'success',
        pending: 'warning',
        failed: 'danger',
        refunded: 'info',
        canceled: 'danger'
      }
      return colors[status] || 'secondary'
    },
    getPaymentMethodLabel (method) {
      const labels = {
        0: 'Gratuito',
        1: 'Boleto',
        2: 'Cartão de Crédito',
        3: 'PIX'
      }
      return labels[method] || method
    },
    getPaymentMethodColor (method) {
      const colors = {
        0: 'secondary',
        1: 'warning',
        2: 'primary',
        3: 'success'
      }
      return colors[method] || 'secondary'
    },
    openTransactionDetails (transaction) {
      console.log('Abrindo detalhes da transação:', transaction)
      this.selectedTransaction = transaction
      this.transactionModal = true
    }
  }
}
</script>

<style scoped>
.bg-primary-soft {
  background-color: rgba(85, 110, 230, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.bg-success-soft {
  background-color: rgba(52, 195, 143, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.bg-info-soft {
  background-color: rgba(80, 165, 241, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.bg-warning-soft {
  background-color: rgba(242, 184, 39, 0.18) !important;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
}

.avatar-xs {
  height: 2rem;
  width: 2rem;
  min-width: 2rem;
  border-radius: 0.5rem;
}

.avatar-sm {
  height: 3rem;
  width: 3rem;
  min-width: 3rem;
  border-radius: 0.5rem;
}

.font-size-16 {
  font-size: 16px !important;
}
</style>
